import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { fetchCatalogTemplates } from 'modules/template-editor/store/templates/actions';
import { selectCatalogTemplateIds } from 'modules/template-editor/store/templates/selectors';

export type CatalogLoadStatus = 'loading' | 'loaded' | 'error';

export interface CatalogTemplatesState {
  status: CatalogLoadStatus;
  /** Повторить загрузку после сбоя; промис — чтобы кнопка «Повторить» была заблокирована до ответа. */
  retry: () => Promise<void>;
}

/**
 * Каталог «Готовые шаблоны» в слайсе: грузит его, если он ещё пуст, по тем же правилам видимости, что и список
 * (супер-админ видит и шаблоны `superAdmin`). Общий для списка и карточки: карточка по прямой ссылке и после F5
 * видит ровно то, что видно в списке (IMGM-1574).
 *
 * Пустоту проверяет по всему каталогу, а не по отфильтрованной выдаче: поиск без совпадений не должен
 * перезапрашивать каталог.
 */
export function useCatalogTemplates(): CatalogTemplatesState {
  const { t } = useLingui();
  const dispatchWithToast = useDispatchWithToast();
  const isAdmin = useIsAdmin();
  const hasCatalog = useSelector(selectCatalogTemplateIds).length > 0;
  const [status, setStatus] = useState<CatalogLoadStatus>(hasCatalog ? 'loaded' : 'loading');
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setStatus('loading');
    const catalogTemplates = await dispatchWithToast(
      fetchCatalogTemplates({ visibility: isAdmin ? ['superAdmin', 'all'] : ['all'] }),
      {
        // Пустой ответ (`null`) раньше тоже считался сбоем — сохраняем это поведение.
        success: (templates) => (templates ? undefined : t`Не удалось загрузить шаблоны каталога`),
        error: msg`Не удалось загрузить шаблоны каталога`,
      },
    );
    if (isMounted.current) setStatus(catalogTemplates ? 'loaded' : 'error');
  }, [dispatchWithToast, isAdmin, t]);

  useEffect(() => {
    if (hasCatalog) {
      setStatus('loaded');
      return;
    }
    load();
  }, [hasCatalog, load]);

  return { status, retry: load };
}
